// src/components/ErrorBoundary.js
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now() + Math.random().toString(36).substr(2, 9)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and potentially send to error reporting service
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // In production, you would send this to your error reporting service
    // this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Example error reporting - replace with your service (Sentry, LogRocket, etc.)
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.props.userId || 'anonymous'
    };
    
    // Send to your error reporting service
    console.log('Would send to error service:', errorReport);
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Different fallback UIs based on error type and context
      const { fallback: CustomFallback, level = 'component' } = this.props;
      
      if (CustomFallback) {
        return (
          <CustomFallback 
            error={this.state.error}
            retry={this.handleRetry}
            reload={this.handleReload}
            goHome={this.handleGoHome}
          />
        );
      }

      // Default fallback UI based on error level
      if (level === 'app') {
        return <AppLevelErrorFallback 
          error={this.state.error}
          errorId={this.state.errorId}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />;
      }

      if (level === 'page') {
        return <PageLevelErrorFallback 
          error={this.state.error}
          errorId={this.state.errorId}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
        />;
      }

      // Component level (default)
      return <ComponentLevelErrorFallback 
        error={this.state.error}
        errorId={this.state.errorId}
        onRetry={this.handleRetry}
      />;
    }

    return this.props.children;
  }
}

// App-level error fallback (for catastrophic failures)
const AppLevelErrorFallback = ({ error, errorId, onRetry, onReload }) => (
  <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <div className="text-center">
          {/* Error Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          {/* Error Content */}
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 mb-6">
            The application encountered an unexpected error. Don't worry, your data is safe.
          </p>
          
          {/* Error ID */}
          <div className="bg-gray-50 rounded-lg p-3 mb-6">
            <p className="text-xs text-gray-500">Error ID: {errorId}</p>
          </div>
          
          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={onRetry}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={onReload}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Reload Page
            </button>
          </div>
          
          {/* Help Text */}
          <p className="text-xs text-gray-500 mt-6">
            If this problem persists, please contact support with the error ID above.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Page-level error fallback
const PageLevelErrorFallback = ({ error, errorId, onRetry, onGoHome }) => (
  <div className="max-w-4xl mx-auto px-4 py-16">
    <div className="text-center">
      {/* Error Icon */}
      <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
        <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      
      {/* Error Content */}
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        Page Error
      </h1>
      <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
        This page encountered an error and couldn't load properly. You can try refreshing or go back to the dashboard.
      </p>
      
      {/* Error Details (collapsible) */}
      <details className="text-left bg-gray-50 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
        <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
          Technical Details
        </summary>
        <div className="mt-2 text-sm text-gray-600">
          <p><strong>Error ID:</strong> {errorId}</p>
          <p><strong>Message:</strong> {error?.message || 'Unknown error'}</p>
        </div>
      </details>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={onRetry}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={onGoHome}
          className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  </div>
);

// Component-level error fallback (most common)
const ComponentLevelErrorFallback = ({ error, errorId, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-6 m-4">
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div className="ml-3 flex-1">
        <h3 className="text-sm font-medium text-red-800">
          Component Error
        </h3>
        <p className="text-sm text-red-700 mt-1">
          This section couldn't load properly. You can try refreshing this component.
        </p>
        <div className="mt-3 flex items-center space-x-4">
          <button
            onClick={onRetry}
            className="text-sm bg-red-100 hover:bg-red-200 text-red-800 font-medium py-1 px-3 rounded transition-colors"
          >
            Retry
          </button>
          <span className="text-xs text-red-600">Error ID: {errorId}</span>
        </div>
      </div>
    </div>
  </div>
);

// Higher-order component for easy wrapping
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for triggering errors (for testing)
export const useErrorHandler = () => {
  return (error) => {
    throw error;
  };
};

export default ErrorBoundary;